import { Injectable, inject } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, EMPTY, combineLatest } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { UserQuery } from '../../auth/state/user.query';

/**
 * Pure check: can a user holding `userRoles` reach a route guarded by
 * `requiredRoles`? A route with no role restriction is reachable by anyone.
 */
export function rolesIntersect(requiredRoles: string[] | undefined, userRoles: string[]): boolean {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }
  return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * Preloads a lazy route only when the signed-in user's roles can reach it.
 * Preloading is deferred to browser idle time so it never competes with the
 * initial render. PreloadAllModules is intentionally NOT used — it would
 * re-download every feature, merely deferred.
 */
@Injectable({ providedIn: 'root' })
export class RolePreloadStrategy implements PreloadingStrategy {
  private userQuery = inject(UserQuery);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const requiredRoles = route.data?.['roles'] as string[] | undefined;

    return combineLatest([this.userQuery.orgUser$, this.userQuery.initialized$]).pipe(
      filter(([, initialized]) => initialized),
      take(1),
      switchMap(([orgUser]) => {
        if (!rolesIntersect(requiredRoles, orgUser?.roles ?? [])) {
          return EMPTY;
        }
        return this.whenIdle(load);
      })
    );
  }

  private whenIdle(load: () => Observable<unknown>): Observable<unknown> {
    return new Observable<unknown>((subscriber) => {
      const win = window as Window & {
        requestIdleCallback?: (cb: () => void) => number;
        cancelIdleCallback?: (handle: number) => void;
      };
      const run = () => load().subscribe(subscriber);
      const handle =
        typeof win.requestIdleCallback === 'function'
          ? win.requestIdleCallback(run)
          : (window.setTimeout(run, 0) as unknown as number);
      return () => {
        if (typeof win.cancelIdleCallback === 'function') {
          win.cancelIdleCallback(handle);
        } else {
          window.clearTimeout(handle);
        }
      };
    });
  }
}
